import React, { useMemo } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

export type CrmFunnelStage = {
  name: string;
  monto: number;
  cantidad: number;
};

const FUNNEL_COLORS = [
  '#9db8c9',
  '#7a9a8b',
  '#c4a882',
  '#e39b7a',
  '#8fa8b8',
  '#6d8f7e',
  '#b8956f',
  '#d4896a',
];

function formatMontoFunnel(amount: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(amount);
}

type Props = {
  stages: CrmFunnelStage[];
  height?: number;
};

type SegGeom = CrmFunnelStage & {
  d: string;
  fill: string;
  cx: number;
  cy: number;
  viewW: number;
  viewH: number;
};

/** Embudo geométrico estilo “pipeline” (trapecios apilados). */
const CrmMontoFunnel: React.FC<Props> = ({ stages, height = 320 }) => {
  const geometry = useMemo((): SegGeom[] => {
    const n = stages.length;
    if (n === 0) return [];

    const viewW = 400;
    const viewH = height;
    const padX = 8;
    const padY = 4;
    const gap = 2;
    const usableH = viewH - padY * 2 - gap * Math.max(0, n - 1);
    const segmentH = usableH / n;
    const topMax = viewW - padX * 2;
    const stemMin = Math.max(topMax * 0.22, 72);

    return stages.map((stage, i) => {
      const tTop = n === 1 ? 0 : i / (n - 1);
      const tBot = n === 1 ? 1 : (i + 1) / (n - 1);
      const ease = (t: number) => {
        if (t <= 0.5) return t * 1.15;
        return 0.575 + (t - 0.5) * 0.85;
      };
      const widthTop = topMax - (topMax - stemMin) * Math.min(1, ease(tTop));
      const widthBot = topMax - (topMax - stemMin) * Math.min(1, ease(tBot));
      const y0 = padY + i * (segmentH + gap);
      const y1 = y0 + segmentH;
      const xTop0 = (viewW - widthTop) / 2;
      const xTop1 = xTop0 + widthTop;
      const xBot0 = (viewW - widthBot) / 2;
      const xBot1 = xBot0 + widthBot;
      const rTop = i === 0 ? 14 : 0;
      const rBot = i === n - 1 ? 14 : 0;

      let d: string;
      if (i === 0 && rTop > 0) {
        d = [
          `M ${xTop0 + rTop} ${y0}`,
          `L ${xTop1 - rTop} ${y0}`,
          `Q ${xTop1} ${y0} ${xTop1} ${y0 + rTop}`,
          `L ${xBot1} ${y1}`,
          `L ${xBot0} ${y1}`,
          `L ${xTop0} ${y0 + rTop}`,
          `Q ${xTop0} ${y0} ${xTop0 + rTop} ${y0}`,
          'Z',
        ].join(' ');
      } else if (i === n - 1 && rBot > 0) {
        d = [
          `M ${xTop0} ${y0}`,
          `L ${xTop1} ${y0}`,
          `L ${xBot1} ${y1 - rBot}`,
          `Q ${xBot1} ${y1} ${xBot1 - rBot} ${y1}`,
          `L ${xBot0 + rBot} ${y1}`,
          `Q ${xBot0} ${y1} ${xBot0} ${y1 - rBot}`,
          'Z',
        ].join(' ');
      } else {
        d = `M ${xTop0} ${y0} L ${xTop1} ${y0} L ${xBot1} ${y1} L ${xBot0} ${y1} Z`;
      }

      return {
        ...stage,
        d,
        fill: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
        cx: viewW / 2,
        cy: (y0 + y1) / 2,
        viewW,
        viewH,
      };
    });
  }, [stages, height]);

  if (stages.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        Sin datos de embudo para los filtros aplicados.
      </Typography>
    );
  }

  const viewW = geometry[0]?.viewW ?? 400;
  const viewH = geometry[0]?.viewH ?? height;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch" sx={{ height }}>
      <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
        <svg
          viewBox={`0 0 ${viewW} ${viewH}`}
          width="100%"
          height="100%"
          role="img"
          aria-label="Embudo de montos por etapa"
        >
          {geometry.map((seg) => {
            const tipCantidad =
              seg.cantidad === 1
                ? '1 oportunidad'
                : `${seg.cantidad} oportunidades`;
            return (
              <g key={seg.name}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {seg.name}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {tipCantidad}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                  followCursor
                >
                  <path
                    d={seg.d}
                    fill={seg.fill}
                    style={{ cursor: 'pointer' }}
                  />
                </Tooltip>
                <text
                  x={seg.cx}
                  y={seg.cy}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#fff"
                  style={{
                    fontSize: stages.length > 6 ? 13 : 15,
                    fontWeight: 600,
                    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                    pointerEvents: 'none',
                  }}
                >
                  {formatMontoFunnel(seg.monto)}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
      <Stack spacing={0.75} justifyContent="center" sx={{ minWidth: { sm: 140 }, flexShrink: 0 }}>
        {geometry.map((seg) => (
          <Stack key={seg.name} direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 0.5,
                bgcolor: seg.fill,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" color="text.secondary" noWrap title={seg.name}>
              {seg.name}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export default CrmMontoFunnel;
